import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Switch from "@mui/material/Switch";
import CloseIcon from "@mui/icons-material/Close";
import type {
  NotificationSetting,
  NotificationTarget,
  NotificationPreview,
} from "../types/types.ts";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import { NotificationTargetFilter } from "./NotificationTargetFilter.tsx";
import {
  fetchNotificationSetting,
  saveNotificationSetting,
  previewNotification,
} from "../utils/notificationSettingsApi.ts";

const ALWAYS_EXCLUDED_STATUSES = ["完了", "非表示"];
const DEFAULT_STATUSES = ["未対応", "対応中", "待機"];
const PRIORITIES = ["高", "中", "低", "なし"];
const BEFORE_DAYS_OPTIONS = Array.from({ length: 30 }, (_, i) => i + 1);
const AFTER_INTERVAL_OPTIONS = [
  { value: 1, label: "毎日" },
  { value: 2, label: "2日おき" },
  { value: 3, label: "3日おき" },
  { value: 7, label: "1週間おき" },
];
const SEND_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);
// チェックボックスを続けて操作したときに毎回 PUT しないよう、変更が落ち着いてから保存する
const AUTO_SAVE_DELAY_MS = 600;

type SaveState = "idle" | "saving" | "saved" | "error";

type SectionKey = "before" | "due" | "after";
const SECTION_LABELS: Record<SectionKey, string> = {
  before: "期限前の通知",
  due: "期限日の通知",
  after: "期限後の通知",
};

interface NotificationSettingsPanelProps {
  roomId: number;
  honbuId: number;
  todoLists: { id: string; name: string }[];
  roomStatuses: { statusName: string }[];
  color: PaletteColor;
  /** 自動保存が完了したとき。「保存しました」はダイアログのフッターに出す */
  onSaved?: () => void;
}

/**
 * 全体設定「通知設定」タブ（F018 期限通知）。
 * 基本は 3 種類の通知の ON/OFF だけで使え、絞り込みは「詳細な設定」に畳んでおく。
 * 変更は自動保存する（他の設定タブと同じく保存ボタンなし）。
 * 詳細: docs/design/F018_deadline-notification.md
 */
export const NotificationSettingsPanel: React.FC<NotificationSettingsPanelProps> = ({
  roomId,
  honbuId,
  todoLists,
  roomStatuses,
  color,
  onSaved,
}) => {
  const [setting, setSetting] = useState<NotificationSetting | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [preview, setPreview] = useState<NotificationPreview | null>(null);
  // 最後にサーバーへ保存（取得）できた内容。差分があるときだけ保存する
  const savedSnapshotRef = useRef("");
  // パネルを閉じた瞬間に待機中の変更を保存するため、最新の設定を保持する
  const latestSettingRef = useRef<NotificationSetting | null>(null);
  // 保存が前後して返ってきた場合に、古い結果で表示を上書きしないための連番
  const saveSeqRef = useRef(0);

  const accent = PaletteColorHex[color];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchNotificationSetting(roomId, honbuId);
        if (cancelled) return;
        savedSnapshotRef.current = JSON.stringify(data);
        setSetting(data);
      } catch (e) {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : "通知設定の取得に失敗しました");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roomId, honbuId]);

  const selectableStatuses = useMemo(() => {
    const names = roomStatuses
      .map((s) => s.statusName)
      .filter((n) => !ALWAYS_EXCLUDED_STATUSES.includes(n));
    return names.length > 0 ? names : DEFAULT_STATUSES;
  }, [roomStatuses]);

  const performSave = useCallback(
    async (target: NotificationSetting) => {
      const json = JSON.stringify(target);
      if (json === savedSnapshotRef.current) return;
      const seq = ++saveSeqRef.current;
      setSaveState("saving");
      setSaveError(null);
      try {
        // レスポンスで state を上書きすると保存中に行った編集が消えるため、送った内容を保存済みとみなす
        await saveNotificationSetting(roomId, honbuId, target);
        savedSnapshotRef.current = json;
        if (seq === saveSeqRef.current) {
          setSaveState("saved");
          onSaved?.();
        }
      } catch (e) {
        if (seq === saveSeqRef.current) {
          setSaveState("error");
          setSaveError(e instanceof Error ? e.message : "通知設定の保存に失敗しました");
        }
      }
    },
    [roomId, honbuId, onSaved],
  );

  // 変更を検知して自動保存
  useEffect(() => {
    latestSettingRef.current = setting;
    if (!setting || !setting.available) return;
    if (JSON.stringify(setting) === savedSnapshotRef.current) return;
    const timer = setTimeout(() => void performSave(setting), AUTO_SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [setting, performSave]);

  // 待機中（デバウンス中）にパネルが閉じられても変更を失わないよう、アンマウント時に保存する
  useEffect(
    () => () => {
      const pending = latestSettingRef.current;
      if (pending && pending.available) void performSave(pending);
    },
    [performSave],
  );

  if (loadError) {
    return <p className="p-4 text-sm text-red-500">{loadError}</p>;
  }
  if (!setting) {
    return <p className="p-4 text-sm text-gray-500 dark:text-gray-400">読み込み中...</p>;
  }

  if (!setting.available) {
    return (
      <div className="p-4">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4 text-sm text-gray-600 dark:text-gray-300">
          MyTODO の通知は個別通知の対応後に利用できます。
        </div>
      </div>
    );
  }

  const updateSection = <K extends SectionKey>(
    key: K,
    patch: Partial<NotificationSetting[K]>,
  ) => {
    setSetting((prev) =>
      prev ? ({ ...prev, [key]: { ...prev[key], ...patch } } as NotificationSetting) : prev,
    );
  };

  // ON の通知のうち、詳細な設定でいずれかの軸をすべて外してしまっているもの
  const sectionsWithoutTarget = (Object.keys(SECTION_LABELS) as SectionKey[]).filter((key) => {
    const t: NotificationTarget = setting[key];
    if (!t.enabled) return false;
    const listIds = todoLists.map((l) => Number(l.id)).filter((id) => id > 0);
    const allExcluded = <T,>(options: T[], excluded: T[]) =>
      options.length > 0 && options.every((o) => excluded.includes(o));
    return (
      allExcluded(listIds, t.excludedListIds) ||
      allExcluded(selectableStatuses, t.excludedStatuses) ||
      allExcluded(PRIORITIES, t.excludedPriorities)
    );
  });

  const handlePreview = async () => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      setPreview(await previewNotification(roomId, honbuId, setting));
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : "通知内容の確認に失敗しました");
    } finally {
      setPreviewing(false);
    }
  };

  const selectClass =
    "mx-1 px-1.5 py-0.5 border rounded bg-white dark:bg-gray-800 dark:border-gray-600 text-sm";

  return (
    <div className="p-4 space-y-4">
      <div className="bg-blue-50 dark:bg-[#1e2733] rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed border border-blue-100 dark:border-[#2d3f52]">
        <p className="font-semibold mb-1">通知設定とは</p>
        <p>
          期限が近いタスク・期限当日のタスク・期限切れのタスクを、毎日決まった時刻にまとめてルームへ通知します。
          通知を ON にするだけで使えます。完了・非表示のタスクは通知されません。
        </p>
        <p className="mt-2">変更は自動で保存されます。</p>
        <p className="mt-2 font-bold">
          ※自動リマインダーは本機能に変更されました。以降は同様の条件で一日に一回まとめて送信が行われます。
        </p>
      </div>

      <NotificationSection
        title={SECTION_LABELS.before}
        enabled={setting.before.enabled}
        onToggle={(enabled) => updateSection("before", { enabled })}
        description={
          <>
            期限の
            <select
              value={setting.before.days}
              onChange={(e) => updateSection("before", { days: Number(e.target.value) })}
              className={selectClass}
              disabled={!setting.before.enabled}
              aria-label="期限の何日前に通知するか"
            >
              {BEFORE_DAYS_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            日前に通知します
          </>
        }
      >
        <NotificationTargetFilter
          value={setting.before}
          onChange={(next) => updateSection("before", next)}
          todoLists={todoLists}
          statuses={selectableStatuses}
          disabled={!setting.before.enabled}
          color={color}
        />
      </NotificationSection>

      <NotificationSection
        title={SECTION_LABELS.due}
        enabled={setting.due.enabled}
        onToggle={(enabled) => updateSection("due", { enabled })}
        description={
          setting.due.target === "start"
            ? "開始日の当日に通知します"
            : setting.due.target === "end"
              ? "期限日の当日に通知します"
              : "開始日・期限日の当日に通知します（同じ日なら 1 回だけ）"
        }
      >
        <NotificationTargetFilter
          value={setting.due}
          onChange={(next) => updateSection("due", next)}
          todoLists={todoLists}
          statuses={selectableStatuses}
          dueTarget={setting.due.target}
          onDueTargetChange={(target) => updateSection("due", { target })}
          disabled={!setting.due.enabled}
          color={color}
        />
      </NotificationSection>

      <NotificationSection
        title={SECTION_LABELS.after}
        enabled={setting.after.enabled}
        onToggle={(enabled) => updateSection("after", { enabled })}
        description={
          <>
            期限の翌日に通知し、以降
            <select
              value={setting.after.intervalDays}
              onChange={(e) =>
                updateSection("after", { intervalDays: Number(e.target.value) })
              }
              className={selectClass}
              disabled={!setting.after.enabled}
              aria-label="期限切れの通知間隔"
            >
              {AFTER_INTERVAL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            通知します（完了するまで）
          </>
        }
      >
        <NotificationTargetFilter
          value={setting.after}
          onChange={(next) => updateSection("after", next)}
          todoLists={todoLists}
          statuses={selectableStatuses}
          disabled={!setting.after.enabled}
          color={color}
        />
      </NotificationSection>

      {/* 共通設定 */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="font-semibold w-20">通知先</span>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              checked={setting.destination === "room"}
              onChange={() => setSetting({ ...setting, destination: "room" })}
              style={{ accentColor: accent }}
            />
            ルーム
          </label>
          {/* 個別通知は今後対応予定のため選択不可 */}
          <label className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
            <input type="radio" disabled />
            個別（準備中）
          </label>
          <label className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
            <input type="radio" disabled />
            両方（準備中）
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="font-semibold w-20">メッセージ</span>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              checked={setting.messageFormat !== "count"}
              onChange={() => setSetting({ ...setting, messageFormat: "detail" })}
              style={{ accentColor: accent }}
            />
            詳細表示
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              checked={setting.messageFormat === "count"}
              onChange={() => setSetting({ ...setting, messageFormat: "count" })}
              style={{ accentColor: accent }}
            />
            件数のみ表示
          </label>
        </div>
        <div className="flex items-center">
          <span className="font-semibold w-20">送信時刻</span>
          <select
            value={setting.sendHour}
            onChange={(e) => setSetting({ ...setting, sendHour: Number(e.target.value) })}
            className={selectClass}
            aria-label="送信時刻"
          >
            {SEND_HOUR_OPTIONS.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
          時
        </div>
      </div>

      {sectionsWithoutTarget.length > 0 && (
        <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-[#2a1a1a] p-3 text-sm text-red-600 dark:text-red-400">
          {sectionsWithoutTarget.map((k) => `「${SECTION_LABELS[k]}」`).join("")}
          は詳細な設定で対象がすべて外れているため、通知されません。
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {saveState === "saving" && (
          <span className="text-sm text-gray-500 dark:text-gray-400">保存中...</span>
        )}
        {saveState === "error" && (
          <span className="text-sm text-red-500">
            {saveError}
            <button
              type="button"
              onClick={() => void performSave(setting)}
              className="ml-2 underline hover:no-underline"
            >
              再試行
            </button>
          </span>
        )}
        {previewError && <span className="text-sm text-red-500">{previewError}</span>}
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewing}
          className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          {previewing ? "確認中..." : "今日の通知内容を確認"}
        </button>
      </div>

      {preview && (
        <NotificationPreviewDialog
          preview={preview}
          color={color}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
};

const NotificationSection: React.FC<{
  title: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  description: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, enabled, onToggle, description, children }) => (
  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-2">
    <div className="flex items-center justify-between">
      <span className="font-semibold">{title}</span>
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-500 dark:text-gray-400">{enabled ? "ON" : "OFF"}</span>
        <Switch
          checked={enabled}
          onChange={(e) => onToggle(e.target.checked)}
          inputProps={{ "aria-label": `${title}の ON/OFF` }}
        />
      </div>
    </div>
    <div
      className={`text-sm ${
        enabled ? "text-gray-700 dark:text-gray-300" : "text-gray-400 dark:text-gray-500"
      }`}
    >
      {description}
    </div>
    {children}
  </div>
);

/** プレビュー結果のダイアログ（SimpleAlertDialog パターン） */
const NotificationPreviewDialog: React.FC<{
  preview: NotificationPreview;
  color: PaletteColor;
  onClose: () => void;
}> = ({ preview, color, onClose }) => {
  const accent = PaletteColorHex[color];
  return (
    <div
      className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-lg max-h-[85vh] flex flex-col rounded-lg shadow-xl overflow-hidden border-2 border-white dark:border-black bg-white dark:bg-[#1e1e1e]"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-4 py-3 text-white"
          style={{ background: `linear-gradient(135deg, ${accent}, ${accent}CC)` }}
        >
          <span className="font-bold text-sm">
            {preview.hasContent ? preview.title : "今日の通知内容"}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-full hover:bg-white/20"
            aria-label="閉じる"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto">
          {preview.hasContent ? (
            <pre className="whitespace-pre-wrap break-words font-sans text-sm text-gray-800 dark:text-gray-200">
              {preview.message}
            </pre>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              今日送られる通知はありません。
            </p>
          )}
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            ※ 現在の設定で表示しています。実際の通知は送信されていません。
          </p>
        </div>
        <div className="flex justify-end px-4 pb-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm font-medium text-white hover:brightness-110"
            style={{ backgroundColor: accent }}
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};
